import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  useAuditReasonConfirm,
  type AuditReasonRequest,
} from '../components/AuditReasonConfirm';
import { PageBody, PageHead } from '../components/Page';
import { Badge } from '../components/Pill';
import { StatusBanner } from '../components/StatusBanner';
import { dateFnsLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import { getUsername } from '../lib/auth';
import { opsErrorMessage } from '../lib/ops-api';
import {
  createOperator,
  disableOperator,
  enableOperator,
  listOperators,
  resetOperatorPassword,
  updateOperator,
  type Operator,
  type OperatorOneTimePassword,
} from '../lib/team-api';

const QUERY_KEY = ['ops-operators'];

/**
 * Who can sign in to this dashboard.
 *
 * No roles, as everywhere here: any operator may add a colleague, reset a forgotten password or
 * disable a leaver's account, and each of those is durably audited with a typed reason. A new or
 * reset account gets a ONE-TIME password, shown once on this page and never again — it does not
 * sign anyone in by itself (the colleague has to replace it at their first sign-in), and that first
 * change is audited with the name of whoever issued it.
 */
export function TeamPage() {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const me = getUsername();
  const [handover, setHandover] = useState<OperatorOneTimePassword | null>(
    null,
  );
  const [editing, setEditing] = useState<Operator | null>(null);
  const [actionError, setActionError] = useState('');
  const { ask, dialog } = useAuditReasonConfirm();

  const operators = useQuery({ queryKey: QUERY_KEY, queryFn: listOperators });
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  /** Runs an audited row action and reports a failure above the table. */
  async function run(action: () => Promise<unknown>): Promise<void> {
    setActionError('');
    try {
      await action();
    } catch (error) {
      setActionError(opsErrorMessage(error, t('team.actionFailed')));
    } finally {
      refresh();
    }
  }

  function askReset(operator: Operator) {
    ask({
      title: t('team.reset.title', { name: operator.displayName }),
      confirmLabel: t('team.reset.confirm'),
      body: t('team.reset.body'),
      onConfirm: (reason) =>
        void run(async () => {
          setHandover(await resetOperatorPassword(operator.username, reason));
        }),
    });
  }

  function askToggle(operator: Operator) {
    const disable = !operator.disabled;
    ask({
      title: t(disable ? 'team.disable.title' : 'team.enable.title', {
        name: operator.displayName,
      }),
      confirmLabel: t(disable ? 'team.disable.confirm' : 'team.enable.confirm'),
      body: t(disable ? 'team.disable.body' : 'team.enable.body'),
      onConfirm: (reason) =>
        void run(() =>
          disable
            ? disableOperator(operator.username, reason)
            : enableOperator(operator.username, reason),
        ),
    });
  }

  return (
    <>
      <PageHead
        eyebrow={t('team.eyebrow')}
        title={t('team.title')}
        lead={t('team.lead')}
      />
      <PageBody>
        {handover && (
          <OneTimePasswordCard
            result={handover}
            onDismiss={() => setHandover(null)}
          />
        )}

        <CreateOperatorForm
          ask={ask}
          onCreated={(result) => {
            setHandover(result);
            refresh();
          }}
        />

        {editing && (
          <EditOperatorForm
            key={editing.username}
            operator={editing}
            ask={ask}
            onDone={() => {
              setEditing(null);
              refresh();
            }}
          />
        )}

        <section className="card space-y-3">
          <h2 className="text-base font-semibold text-gray-900">
            {t('team.list.title')}
          </h2>
          {actionError && (
            <StatusBanner tone="error">{actionError}</StatusBanner>
          )}
          {operators.isError ? (
            <StatusBanner tone="error">
              {opsErrorMessage(operators.error, t('team.list.loadFailed'))}
            </StatusBanner>
          ) : !operators.data ? (
            <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('team.list.operator')}</th>
                    <th>{t('team.list.state')}</th>
                    <th>{t('team.list.lastLogin')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {operators.data.map((operator) => (
                    <OperatorRow
                      key={operator.username}
                      operator={operator}
                      isMe={operator.username === me}
                      onEdit={() => setEditing(operator)}
                      onReset={() => askReset(operator)}
                      onToggle={() => askToggle(operator)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        {dialog}
      </PageBody>
    </>
  );
}

function OperatorRow({
  operator,
  isMe,
  onEdit,
  onReset,
  onToggle,
}: {
  readonly operator: Operator;
  readonly isMe: boolean;
  readonly onEdit: () => void;
  readonly onReset: () => void;
  readonly onToggle: () => void;
}) {
  const { t, locale } = useLocale();
  const when = (iso: string | null | undefined) =>
    iso
      ? format(new Date(iso), 'dd MMM yyyy HH:mm', {
          locale: dateFnsLocale(locale),
        })
      : '—';

  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900">
          {operator.displayName}
        </span>
        {isMe && (
          <span className="ml-2">
            <Badge tone="teal">{t('team.list.you')}</Badge>
          </span>
        )}
        <p className="text-xs text-gray-500">
          <span className="font-mono">{operator.username}</span>
          {operator.email
            ? ` · ${operator.email}`
            : ` · ${t('team.list.noEmail')}`}
        </p>
      </td>
      <td className="whitespace-nowrap space-x-1">
        {operator.disabled ? (
          <Badge tone="gray">{t('team.state.disabled')}</Badge>
        ) : (
          <Badge tone="green">{t('team.state.active')}</Badge>
        )}
        {operator.locked && <Badge tone="red">{t('team.state.locked')}</Badge>}
        {operator.mustChangePassword && !operator.disabled && (
          <Badge tone="amber">{t('team.state.oneTime')}</Badge>
        )}
      </td>
      <td className="whitespace-nowrap text-sm text-gray-600">
        {when(operator.lastLoginAt)}
      </td>
      <td className="whitespace-nowrap text-right space-x-2">
        <button type="button" className="btn-secondary btn-sm" onClick={onEdit}>
          {t('team.list.edit')}
        </button>
        {!operator.disabled && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={onReset}
          >
            {t('team.list.reset')}
          </button>
        )}
        {/* An operator cannot disable themselves — the API refuses it too. */}
        {!isMe && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={onToggle}
          >
            {operator.disabled ? t('team.list.enable') : t('team.list.disable')}
          </button>
        )}
      </td>
    </tr>
  );
}

/** The one place a one-time password is ever shown. */
function OneTimePasswordCard({
  result,
  onDismiss,
}: {
  readonly result: OperatorOneTimePassword;
  readonly onDismiss: () => void;
}) {
  const { t } = useLocale();
  const [copied, setCopied] = useState(false);

  return (
    <section className="card space-y-3 border-amber-300 bg-amber-50">
      <h2 className="text-base font-semibold text-gray-900">
        {t('team.handover.title', { name: result.operator.displayName })}
      </h2>
      <p className="text-sm text-gray-700">
        {t('team.handover.lead', { username: result.operator.username })}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <code className="font-mono text-base bg-white px-3 py-2 rounded border border-amber-300 select-all break-all">
          {result.oneTimePassword}
        </code>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() =>
            void navigator.clipboard
              .writeText(result.oneTimePassword)
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }
        >
          {copied ? t('team.handover.copied') : t('team.handover.copy')}
        </button>
      </div>
      <p className="text-xs text-gray-600">{t('team.handover.warning')}</p>
      <button type="button" className="btn-primary btn-sm" onClick={onDismiss}>
        {t('team.handover.dismiss')}
      </button>
    </section>
  );
}

/** Opens the shared confirm-with-reason dialog. */
type Ask = (request: AuditReasonRequest) => void;

function CreateOperatorForm({
  ask,
  onCreated,
}: {
  readonly ask: Ask;
  readonly onCreated: (result: OperatorOneTimePassword) => void;
}) {
  const { t } = useLocale();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');

  const create = useMutation({
    mutationFn: createOperator,
    onSuccess: (result) => {
      onCreated(result);
      setUsername('');
      setDisplayName('');
      setEmail('');
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const input = {
      username: username.trim().toLowerCase(),
      displayName: displayName.trim(),
      email,
    };
    ask({
      title: t('team.create.confirmTitle', { name: input.displayName }),
      confirmLabel: t('team.create.submit'),
      body: t('team.create.confirmBody', { username: input.username }),
      onConfirm: (reason) => create.mutate({ ...input, reason }),
    });
  }

  const ready = username.trim().length >= 2 && displayName.trim().length > 0;

  return (
    <section className="card space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">
          {t('team.create.title')}
        </h2>
        <p className="text-sm text-gray-600">{t('team.create.lead')}</p>
      </div>
      <form onSubmit={submit} className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="field-label" htmlFor="operator-username">
            {t('team.create.username')}
          </label>
          <input
            id="operator-username"
            className="field-input font-mono"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoCapitalize="none"
            autoComplete="off"
            maxLength={32}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t('team.create.usernameHint')}
          </p>
        </div>
        <div>
          <label className="field-label" htmlFor="operator-name">
            {t('team.create.displayName')}
          </label>
          <input
            id="operator-name"
            className="field-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={100}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="operator-email">
            {t('team.create.email')}
          </label>
          <input
            id="operator-email"
            type="email"
            className="field-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
          />
        </div>
        {create.isError && (
          <div className="md:col-span-3">
            <StatusBanner tone="error">
              {opsErrorMessage(create.error, t('team.actionFailed'))}
            </StatusBanner>
          </div>
        )}
        <div className="md:col-span-3">
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={!ready || create.isPending}
          >
            {t('team.create.submit')}
          </button>
        </div>
      </form>
    </section>
  );
}

function EditOperatorForm({
  operator,
  ask,
  onDone,
}: {
  readonly operator: Operator;
  readonly ask: Ask;
  readonly onDone: () => void;
}) {
  const { t } = useLocale();
  const [displayName, setDisplayName] = useState(operator.displayName);
  const [email, setEmail] = useState(operator.email);

  const save = useMutation({
    mutationFn: (reason: string) =>
      updateOperator(operator.username, {
        displayName: displayName.trim(),
        email,
        reason,
      }),
    onSuccess: onDone,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    ask({
      title: t('team.edit.title', { username: operator.username }),
      confirmLabel: t('team.edit.submit'),
      body: t('team.edit.confirmBody'),
      onConfirm: (reason) => save.mutate(reason),
    });
  }

  return (
    <section className="card space-y-4">
      <h2 className="text-base font-semibold text-gray-900">
        {t('team.edit.title', { username: operator.username })}
      </h2>
      <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="edit-name">
            {t('team.create.displayName')}
          </label>
          <input
            id="edit-name"
            className="field-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={100}
          />
        </div>
        <div>
          <label className="field-label" htmlFor="edit-email">
            {t('team.create.email')}
          </label>
          <input
            id="edit-email"
            type="email"
            className="field-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t('team.edit.emailHint')}
          </p>
        </div>
        {save.isError && (
          <div className="md:col-span-2">
            <StatusBanner tone="error">
              {opsErrorMessage(save.error, t('team.actionFailed'))}
            </StatusBanner>
          </div>
        )}
        <div className="md:col-span-2 flex gap-2">
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={!displayName.trim() || save.isPending}
          >
            {t('team.edit.submit')}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={onDone}
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </section>
  );
}
